import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import api from '../../services/api';
import { isSnapshotBuilding, snapshotRetryDelayMs } from '../../services/snapshotState';
import { clearbitLogoFromWebsite } from '../../utils/clearbitLogo';
import { exportCsv } from '../../utils/csvExport';
import { chartSeries, tokens } from './marketingTokens';
import NoClientState from './NoClientState';
import './ai-vis.css';

const PAGE_SIZE = 20;

const LINE_COLORS = chartSeries;

const COL_TOOLTIPS = {
  domain: 'The root domain of the source URL that was cited in AI responses.',
  classifier:
    'How this domain is classified — Corporate (brand/company sites), UGC (User Generated Content, e.g. Reddit, Quora, forums), Editorial (content publications & roundups), You (your own domains), Review, Review site, Ecommerce, Video, or Other.',
  citation_share:
    '% of AI outputs where this domain was cited at least once. A domain cited once vs. multiple times in the same output counts equally.',
  // label renamed to Citation Rate — key kept as citation_share for data compatibility
  total_citations:
    'Number of AI outputs that cited this domain at least once.',
};

function formatShortDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function SearchIcon() {
  return (
    <span className="aivis-prompts-search-icon" aria-hidden>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path
          d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function SortIcon({ active, dir }) {
  if (active) {
    return (
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden
        className="aivis-domains-sort-icon aivis-domains-sort-icon--active"
      >
        {dir === 'asc' ? (
          <path d="M6 2L10.5 9H1.5L6 2Z" fill="currentColor" />
        ) : (
          <path d="M6 10L1.5 3H10.5L6 10Z" fill="currentColor" />
        )}
      </svg>
    );
  }
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden
      className="aivis-domains-sort-icon"
    >
      <path d="M3.5 5L6 2L8.5 5H3.5Z" fill="currentColor" opacity="0.4" />
      <path d="M3.5 7L6 10L8.5 7H3.5Z" fill="currentColor" opacity="0.4" />
    </svg>
  );
}

const TOOLTIP_W = 230;
const TOOLTIP_MARGIN = 12;

function TooltipPortal({ text, anchorRef, visible }) {
  const [style, setStyle] = useState({});

  useEffect(() => {
    if (!visible || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const anchorCX = r.left + r.width / 2;

    // Ideal left: center tooltip over anchor
    let left = anchorCX - TOOLTIP_W / 2;
    // Clamp so it never overflows either edge
    left = Math.max(TOOLTIP_MARGIN, Math.min(left, window.innerWidth - TOOLTIP_W - TOOLTIP_MARGIN));

    // Arrow should still point at the anchor center
    const arrowLeft = Math.round(
      Math.max(14, Math.min(anchorCX - left, TOOLTIP_W - 14))
    );

    setStyle({
      left,
      top: r.top - 10,
      transform: 'translateY(-100%)',
      '--arrow-left': `${arrowLeft}px`,
    });
  }, [visible, anchorRef]);

  if (!visible || !text) return null;
  return createPortal(
    <div className="aivis-domains-tooltip-popup" style={style} role="tooltip">
      {text}
    </div>,
    document.body
  );
}

function InfoTooltip({ text }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  return (
    <span
      ref={ref}
      className="aivis-domains-th-info"
      aria-label={text}
      role="img"
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
      tabIndex={0}
    >
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden>
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M8 7v5M8 5.25V5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
      <TooltipPortal text={text} anchorRef={ref} visible={visible} />
    </span>
  );
}

function ColHeader({ label, sortKey, currentSort, currentDir, onSort, tooltip, numeric }) {
  const active = currentSort === sortKey;
  return (
    <th
      scope="col"
      className={`${numeric ? 'aivis-domains-th-num' : ''} ${sortKey ? 'aivis-domains-th-sortable' : ''}`}
      onClick={sortKey ? () => onSort(sortKey) : undefined}
      aria-sort={active ? (currentDir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <div className="aivis-domains-th-inner">
        <span className="aivis-domains-th-label">{label}</span>
        {sortKey && <SortIcon active={active} dir={currentDir} />}
        {tooltip && <InfoTooltip text={tooltip} />}
      </div>
    </th>
  );
}

function PaginationControls({ page, totalPages, totalItems, onPage }) {
  const pages = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (page > 3) pages.push('…');
    const lo = Math.max(2, page - 1);
    const hi = Math.min(totalPages - 1, page + 1);
    for (let i = lo; i <= hi; i++) pages.push(i);
    if (page < totalPages - 2) pages.push('…');
    pages.push(totalPages);
  }

  return (
    <div className="aivis-domains-pagination">
      <div className="aivis-domains-pg-controls">
        <button
          type="button"
          className="aivis-domains-pg-btn"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          aria-label="Previous page"
        >
          ‹
        </button>
        {pages.map((p, idx) =>
          p === '…' ? (
            <span key={`e${idx}`} className="aivis-domains-pg-ellipsis">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              className={`aivis-domains-pg-btn ${p === page ? 'aivis-domains-pg-btn--active' : ''}`}
              onClick={() => onPage(p)}
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          className="aivis-domains-pg-btn"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
          aria-label="Next page"
        >
          ›
        </button>
      </div>
      <span className="aivis-domains-pg-count">
        {totalItems.toLocaleString()} item{totalItems === 1 ? '' : 's'}
      </span>
    </div>
  );
}

export default function DomainsPage() {
  const { selectedCampaignId, campaigns } = useOutletContext();
  const selected = campaigns.find((c) => String(c.id) === String(selectedCampaignId));

  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState('total_citations');
  const [sortDir, setSortDir] = useState('desc');
  const [snapshotRetry, setSnapshotRetry] = useState(0);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    setSearch('');
    setDebouncedSearch('');
    setPage(1);
    setData(null);
    setError('');
  }, [selectedCampaignId]);

  useEffect(() => {
    if (!selectedCampaignId) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(PAGE_SIZE),
      sort: sortKey,
      direction: sortDir,
    });
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
    api
      .get(`/aivis/campaigns/${selectedCampaignId}/domains/?${params}`, { signal: controller.signal })
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || 'Could not load domains.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [selectedCampaignId, page, sortKey, sortDir, debouncedSearch, snapshotRetry]);

  const snapshotBuilding = isSnapshotBuilding(data);
  const snapshotRetryAfter = data?.retry_after;

  useEffect(() => {
    if (!snapshotBuilding) return undefined;
    const timer = window.setTimeout(
      () => setSnapshotRetry((attempt) => attempt + 1),
      snapshotRetryDelayMs({ retry_after: snapshotRetryAfter }),
    );
    return () => window.clearTimeout(timer);
  }, [snapshotBuilding, snapshotRetryAfter]);

  function handleSearchChange(e) {
    const val = e.target.value;
    setSearch(val);
    setPage(1);
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(val);
    }, 320);
  }

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
    setPage(1);
  }

  function handleExportCsv() {
    const clientSlug = selected?.client_name?.toLowerCase().replace(/\s+/g, '-') || 'client';
    const date = new Date().toISOString().slice(0, 10);
    const columns = [
      { header: '#', key: '_rank', format: (_, row) => String(sortedDomains.indexOf(row) + 1) },
      { header: 'Domain', key: 'domain' },
      { header: 'Citation Rate (%)', key: 'citation_share' },
      { header: 'Citations', key: 'total_citations' },
    ];
    exportCsv(columns, sortedDomains, `domains-${clientSlug}-${date}.csv`);
  }

  // Sorting and pagination are server-side so large campaigns never transfer
  // every historical domain just to render one table page.
  const sortedDomains = useMemo(() => {
    return data?.domains || [];
  }, [data?.domains]);

  const totalDomains = data?.domains_count || 0;
  const totalPages = data?.total_pages || 1;
  const safePage = Math.min(page, totalPages);
  const pageDomains = sortedDomains;

  const lineOrder = useMemo(() => data?.series_domain_order || [], [data?.series_domain_order]);
  const chartData = useMemo(
    () =>
      (data?.series || []).map((row) => ({
        date: row.date,
        label: formatShortDate(row.date),
        ...(row.metrics || {}),
      })),
    [data?.series]
  );

  const yMax = useMemo(() => {
    const vals = chartData.flatMap((d) => lineOrder.map((k) => (typeof d[k] === 'number' ? d[k] : 0)));
    const peak = vals.length ? Math.max(...vals) : 0;
    return Math.min(100, Math.ceil((peak + 5) / 5) * 5);
  }, [chartData, lineOrder]);

  const renderTooltip = useCallback(({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="aivis-dash-tooltip">
        <div className="aivis-dash-tooltip-title">{label}</div>
        {payload
          .filter((p) => p.dataKey !== 'label' && p.dataKey !== 'date')
          .map((p) => (
            <div key={String(p.dataKey)} className="aivis-dash-tooltip-row">
              <span className="aivis-dash-tooltip-dot" style={{ background: p.color }} />
              <span>{p.name}</span>
              <strong>{typeof p.value === 'number' ? `${p.value}%` : p.value}</strong>
            </div>
          ))}
      </div>
    );
  }, []);

  if (!selectedCampaignId) {
    return <NoClientState />;
  }

  return (
    <div className="aivis-domains-page">
      <header className="aivis-dash-header">
        <div>
          <h1 className="aivis-page-title">Cited Domains</h1>
          <p className="aivis-page-sub">
            {selected
              ? `Domain-level source visibility for "${selected.client_name}" across AI responses.`
              : 'Select a campaign to view domain stats.'}
          </p>
        </div>
      </header>

      {error ? <div className="aivis-auth-error">{error}</div> : null}
      {snapshotBuilding ? (
        <p className="aivis-page-sub" aria-live="polite">
          Preparing cited-domain insights from completed runs. This page will update automatically.
        </p>
      ) : null}

      {/* Section 1: chart */}
      <section className="aivis-dash-section" aria-label="Overview">
        <div className="aivis-dash-card">
          <div className="aivis-dash-card-head">
            <h2 className="aivis-dash-card-title">Source Usage by Domain</h2>
            <p className="aivis-dash-card-desc">
              % of chats where each top domain appeared as a source (by day).
            </p>
          </div>
          <div className="aivis-dash-chart-wrap aivis-dash-chart-wrap--tall">
            {snapshotBuilding ? (
              <p className="aivis-dash-empty">Gathering cited-domain data from completed runs…</p>
            ) : loading && !chartData.length ? (
              <p className="aivis-dash-empty">Loading…</p>
            ) : chartData.length && lineOrder.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={tokens.graphite200}
                    vertical={false}
                  />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke={tokens.graphite500} />
                  <YAxis
                    domain={[0, yMax]}
                    tick={{ fontSize: 11 }}
                    stroke={tokens.graphite500}
                    width={36}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <Tooltip content={renderTooltip} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {lineOrder.map((key, i) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      name={key}
                      stroke={LINE_COLORS[i % LINE_COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="aivis-dash-empty">
                {selectedCampaignId
                  ? 'No source data yet. Run a campaign to see domain trends.'
                  : 'Select a campaign.'}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Section 2: domains table */}
      <section className="aivis-dash-section" aria-label="Domains table">
        <div className="aivis-dash-card">
          <div className="aivis-domains-table-toolbar">
            <div className="aivis-prompts-search-wrap">
              <SearchIcon />
              <input
                type="search"
                className="aivis-prompts-search"
                placeholder="Search domains"
                value={search}
                onChange={handleSearchChange}
                aria-label="Search domains"
              />
            </div>
            {sortedDomains.length > 0 && (
              <button type="button" className="aivis-csv-export-btn" onClick={handleExportCsv} title="Export visible rows as CSV">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export CSV
              </button>
            )}
          </div>

          <div className="aivis-domains-table-scroll">
            <table className="aivis-domains-table">
              <colgroup>
                <col style={{ width: '4%' }} />
                <col style={{ width: '46%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '25%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className="aivis-domains-th-num">
                    #
                  </th>
                  <ColHeader
                    label="Domain"
                    sortKey="domain"
                    currentSort={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                    tooltip={COL_TOOLTIPS.domain}
                  />
                  <ColHeader
                    label="Citation Rate"
                    sortKey="citation_share"
                    currentSort={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                    tooltip={COL_TOOLTIPS.citation_share}
                    numeric
                  />
                  <ColHeader
                    label="Citations"
                    sortKey="total_citations"
                    currentSort={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                    tooltip={COL_TOOLTIPS.total_citations}
                    numeric
                  />
                </tr>
              </thead>
              <tbody>
                {pageDomains.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="aivis-domains-empty">
                      {snapshotBuilding
                        ? 'Gathering cited-domain data from completed runs…'
                        : loading
                        ? 'Loading…'
                        : search
                          ? 'No domains match your search.'
                          : selectedCampaignId
                            ? 'No domain data yet for this campaign.'
                            : 'Select a campaign.'}
                    </td>
                  </tr>
                ) : (
                  pageDomains.map((row, idx) => {
                    const logoSrc = clearbitLogoFromWebsite(row.domain);
                    return (
                      <tr
                        key={row.domain}
                        className="aivis-domains-tr aivis-domains-tr--clickable"
                        onClick={() => navigate(`/ai-vis/domains/${encodeURIComponent(row.domain)}`)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && navigate(`/ai-vis/domains/${encodeURIComponent(row.domain)}`)}
                      >
                        <td className="aivis-domains-td-num aivis-domains-td-rank">
                          {(safePage - 1) * PAGE_SIZE + idx + 1}
                        </td>
                        <td className="aivis-domains-td-domain">
                          <div className="aivis-domains-domain-cell">
                            {logoSrc ? (
                              <img
                                src={logoSrc}
                                alt=""
                                width={20}
                                height={20}
                                className="aivis-domains-favicon"
                                onError={(e) => {
                                  e.currentTarget.style.visibility = 'hidden';
                                }}
                              />
                            ) : null}
                            <span className="aivis-domains-domain-name">{row.domain}</span>
                          </div>
                        </td>
                        <td className="aivis-domains-td-num">{row.citation_share}%</td>
                        <td className="aivis-domains-td-num">{row.total_citations ?? '—'}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalDomains > PAGE_SIZE ? (
            <PaginationControls
              page={safePage}
              totalPages={totalPages}
              totalItems={totalDomains}
              onPage={(p) => setPage(p)}
            />
          ) : totalDomains > 0 ? (
            <p className="aivis-domains-count">
              {totalDomains.toLocaleString()} domain{totalDomains === 1 ? '' : 's'}
            </p>
          ) : null}
        </div>
      </section>
    </div>
  );
}
